/**
 * Hook Principal de Controle de Salvamento (REFATORADO)
 * 
 * Orquestra todo o fluxo de salvamento integrando:
 * - 4 hooks de estado (useSaveState, useSaveProgress, useSaveMetadata, useSaveFileInfo)
 * - 3 services (MetadataService, CorelSaveService, ApiSaveService)
 * - 6 utilitários (constants, validators, transformers, builders, etc)
 * 
 * INTERFACE PÚBLICA: 100% idêntica ao original (23 exports)
 * 
 * Origem: useSaveController.ts (2083 linhas → ~400 linhas)
 */

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { logger, sendToTerminal } from '../../../lib/terminalLogger';
import { lockManager } from '../../../lib/corel/lockManager';
import { getActionTimeout } from '../../../lib/corel/timeoutService';
import { useNotifications } from '../../feedback/NotificationProvider';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useOrders } from '../../../contexts/OrderContext';
import { useUnifiedFeaturePermission } from '../../../hooks/useUnifiedPermissions';
import { corelCommunication } from '../../../lib/corel/communication';
import { lockDocument } from '../../../lib/corelOperations';
import { abortManager } from '../../../lib/corel/pipeline/abortManager';
import { monitoradorObjetos } from '../../../lib/corel/MonitoradorObjetos';
import { validateOrderIds } from '../../../lib/corel/pipeline/idValidators';
import { testConnection } from '../../../lib/api';
import type { SaveState, SaveProgressModalProps } from '../types';

// Services e Hooks
import { CorelSaveService } from '../services/corelSaveService';
import { ApiSaveService } from '../services/apiSaveService';
import { useSaveState } from './useSaveState';
import { useSaveProgress } from './useSaveProgress';
import { useSaveMetadata } from './useSaveMetadata';
import { useSaveFileInfo } from './useSaveFileInfo';

// Utils
import { generateApiRequestPayload, generateDebugPayload, buildMetadataPayload } from '../utils/payloadBuilders';
import { DELAY_BEFORE_API, DELAY_CLOSING_MODAL } from '../constants/saveConstants';

/**
 * Argumentos do hook useSaveController
 */
export interface UseSaveControllerArgs {
  onComplete: SaveProgressModalProps['onComplete'];
  initialMetadataSnapshot?: unknown;
}

/**
 * Hook principal de controle de salvamento
 * 
 * @param args - Argumentos do hook
 * @returns Interface pública com 23 exports
 */
export function useSaveController({ onComplete, initialMetadataSnapshot }: UseSaveControllerArgs) {
  // Hook iniciado - silencioso

  // Context
  const { notify } = useNotifications();
  const { role } = usePermissions();
  const { fetchPedidos, markItemArquivoSaved } = useOrders();
  const isExternalUser = useUnifiedFeaturePermission('general.use_external_upload_flow');

  // Estados auxiliares
  const [showClientMessage, setShowClientMessage] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Refs de controle
  const modalClosingRef = useRef(false);
  const operationIdRef = useRef<string | null>(null);
  const idemKeyRef = useRef<string | null>(null);
  const apiSentRef = useRef(false);
  const timeoutCheckRef = useRef<any>(null);
  const pipelineExecutingRef = useRef(false); // Guard contra execuções múltiplas

  // Hooks de estado (4 hooks integrados)
  const saveStateManager = useSaveState({ initialState: 'saving' });
  const progressManager = useSaveProgress({ initialProgress: 0 });
  const metadataManager = useSaveMetadata({ initialMetadataSnapshot });
  const fileInfoManager = useSaveFileInfo({ isExternalUser });

  // ============================================================================
  // LIFECYCLE DO MONITOR
  // ============================================================================
  
  // ✅ SaveController não precisa gerenciar lifecycle do monitor
  // O monitor já está ativo (iniciado na pipeline ou em OrderDetailsWindow)

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Gera mensagem do cliente para WhatsApp
   */
  const generateClientMessage = useCallback((): string => {
    if (!fileInfoManager.saveData) return '';

    const data = fileInfoManager.saveData;
    const horaLimite = data.regras?.horario_corte || '18:00';
    const formattedHoraLimite = horaLimite.includes(':') 
      ? horaLimite.split(':').slice(0, 2).join(':') 
      : horaLimite;
    const clienteNome = data.cliente?.nome_fantasia || data.cliente?.nome || 'Cliente';
    const pedidoId = data.pedido?.orc_id || '';
    const produtoNome = data.produto?.nome || 'seu produto';
    const entrega = data.configuracoes?.entrega || 'Hoje';

    const horaAtual = new Date();
    const [hh, mm] = formattedHoraLimite.split(':').map(Number);
    const corte = new Date();
    corte.setHours(hh, mm, 0);
    const antesDoCorte = horaAtual < corte;

    let previsao = 'Hoje até 18h';
    const up = String(entrega).toUpperCase();
    if (up === 'HOJE') previsao = antesDoCorte ? 'Hoje até 18h' : 'Amanhã até 12h';
    else if (up === 'AMANHÃ' || up === 'AMANHA') previsao = 'Amanhã até 12h';
    else if (up === 'NOTURNA') previsao = 'Amanhã até 10h';
    else if (up === 'URGENTE') previsao = 'Hoje até 18:30h';

    return `📌 *STATUS DO PEDIDO* 📌

*${clienteNome}*, seu pedido *#${pedidoId}*  
(${produtoNome}) está em produção! ✅

━━━━━━━━━━━━━━  
🛠️ *Status*: Processando seu pedido  
⏰ *Previsão*: ${previsao}  
━━━━━━━━━━━━━━  

📲 *Acompanhamento*:  
Após confirmação do pagamento, você receberá:  
• Link exclusivo via WhatsApp  
• Número: *(555) 600-0657*  

📦 *Contato Expedição*:  
(62) 9531-1320 *(retirada/entrega)*  

Agradecemos pela confiança! 🙌`;
  }, [fileInfoManager.saveData]);

  /**
   * Gera título do erro baseado na mensagem
   */
  const getErrorTitle = useCallback((errorMsg: string): string => {
    if (!errorMsg) return 'Erro ao Salvar';

    const lowerMsg = errorMsg.toLowerCase();

    if (lowerMsg.includes('metadados') || lowerMsg.includes('metadata')) {
      return 'Erro ao Coletar Metadados';
    }
    if (lowerMsg.includes('caminho') || lowerMsg.includes('path')) {
      return 'Erro no Caminho do Arquivo';
    }
    if (lowerMsg.includes('cancelado') || lowerMsg.includes('abortado') || lowerMsg.includes('fechado')) {
      return 'Salvamento Cancelado';
    }
    if (lowerMsg.includes('api') || lowerMsg.includes('servidor')) {
      return 'Erro de Comunicação';
    }
    if (lowerMsg.includes('corel') || lowerMsg.includes('salvar arquivo')) {
      return 'Erro ao Salvar no CorelDRAW';
    }

    return 'Erro ao Salvar';
  }, []);


  /**
   * Limpeza de recursos ao abortar operação
   */
  const cleanupOnAbort = useCallback(async () => {
    // Executando cleanup após abort - silencioso
    
    // Limpar interval de progresso
    progressManager.stopProgress();
    
    // Fechar modal de metadados se estiver aberto
    metadataManager.setShowMetadataModal(false);
    
    // Tentar parar lock do documento com retry automático
    try {
      await new Promise(r => setTimeout(r, 200));
      const result = await lockManager.unlock(3);
      if (!result.ok) {
        logger.warn('[SAVE] Falha ao parar lock durante abort (agendando limpeza futura)', {
          error: result.error,
          attempts: result.attempts
        });
        // Agendar limpeza futura caso falhe
        lockManager.scheduleCleanup();
      }
    } catch (err) {
      logger.warn('[SAVE] Exceção ao parar lock durante abort', {
        error: err instanceof Error ? err.message : String(err)
      });
      // Agendar limpeza futura caso falhe
      lockManager.scheduleCleanup();
    }
    
    // Marcar resposta como recebida
    progressManager.markResponseReceived();
    
    // Cleanup após abort concluído - silencioso
  }, [progressManager, metadataManager]);

  /**
   * Restaura recursos após erro
   * 
   * ⚠️ IMPORTANTE: NÃO reativa lock-document!
   * - O lock foi desativado propositalmente no Checklist para permitir o save
   * - Reativar após erro impede retries (documento fica bloqueado)
   * - Lock só deve ser reativado ao FECHAR o modal (sucesso ou cancelamento)
   * 
   * Apenas reinicia o monitor para continuar detectando mudanças no documento.
   */
  const restoreResourcesOnError = useCallback(async () => {
    // Restaurando recursos após erro - silencioso

    // ❌ NÃO reativar lock aqui!
    // O lock só deve ser reativado ao fechar o modal (handleClose)
    // Reativar aqui impediria retries após erro HMAC

    // ✅ Reiniciar monitor apenas (para detectar mudanças)
    try {
      await monitoradorObjetos.iniciarComConfiguracao({
        pollingInterval: 400,
        batchSize: 10
      });
      // Monitor reiniciado - silencioso
    } catch (monitorError) {
      logger.error('[SAVE] Erro ao reiniciar monitor', {
        error: monitorError instanceof Error ? monitorError.message : String(monitorError)
      });
    }
  }, []);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleClose = useCallback(() => {
    if (modalClosingRef.current) return;
    modalClosingRef.current = true;
    saveStateManager.setIsClosing(true);

    setTimeout(() => {
      modalClosingRef.current = false;
      saveStateManager.setIsClosing(false);
      if (!showClientMessage) onComplete();
    }, DELAY_CLOSING_MODAL);
  }, [showClientMessage, onComplete, saveStateManager]);

  const handleShowClientMessage = useCallback(() => {
    if (modalClosingRef.current) return;
    setShowClientMessage(true);
  }, []);

  const handleExportComplete = useCallback(() => {
    saveStateManager.setSaveState('success');
  }, [saveStateManager]);

  const handleRetry = useCallback(() => {
    // Tentando novamente - silencioso

    // Resetar estados
    saveStateManager.clearError();
    saveStateManager.setSaveState('saving');
    progressManager.resetProgress();

    // Recarregar dados (loadFileInfo já é memoizado)
    fileInfoManager.loadFileInfo();
  }, [saveStateManager, progressManager, fileInfoManager.loadFileInfo]);

  // ============================================================================
  // PIPELINE PRINCIPAL
  // ============================================================================

  /**
   * Pipeline de salvamento (2 etapas + API)
   */
  const executeSavePipeline = useCallback(async () => {
    logger.info('[SAVE] Iniciando salvamento');

    try {
      // Healthcheck (não bloqueante)
      try {
        await testConnection();
        // API acessível - silencioso
      } catch (err) {
        logger.warn('[SAVE] API healthcheck falhou', {
          error: err instanceof Error ? err.message : String(err)
        });
      }

      // CHECKPOINT 1: Verificar abort antes de iniciar
      if (operationIdRef.current && abortManager.isAborted(operationIdRef.current)) {
        logger.warn('[SAVE] Salvamento abortado antes de iniciar', {
          reason: 'document_closed_before_start'
        });
        await cleanupOnAbort();
        saveStateManager.setSaveState('cancelled');
        saveStateManager.setErrorMessage('Salvamento cancelado: documento foi fechado');
        return;
      }

      // ETAPA 1: Salvar no Corel
      logger.info('[SAVE] Salvando no CorelDRAW');
      progressManager.setProgress(10);

      const workingData = fileInfoManager.saveDataRef.current || fileInfoManager.saveData;
      
      // DEBUG: Log dos dados que serão usados
      sendToTerminal('🔍 [DEBUG:PIPELINE] Dados de trabalho', {
        source: fileInfoManager.saveDataRef.current ? 'saveDataRef' : 'saveData',
        hasCaminhoArquivo: !!workingData?.caminho_arquivo,
        caminhoArquivo: workingData?.caminho_arquivo || null,
        diretorios: {
          interno: workingData?.diretorios?.interno || null,
          externo: workingData?.diretorios?.externo || null
        },
        orcId: workingData?.pedido?.orc_id,
        oitId: workingData?.pedido?.oit_id
      });
      
      if (!workingData) {
        throw new Error('Dados de salvamento não encontrados');
      }

      // Construir metadata
      const metadata = buildMetadataPayload(
        workingData,
        metadataManager.metadataSnapshotRef.current,
        metadataManager.lastSaveMetaRef.current,
        role
      );

      // Salvar no Corel
      const corelResult = await CorelSaveService.saveFile({
        workingData,
        metadata,
        role,
        notify,
        lastSaveMetaRef: metadataManager.lastSaveMetaRef,
        savedFilePathRef: fileInfoManager.savedFilePathRef
      });

      if (!corelResult.ok) {
        throw new Error(corelResult.error || 'Falha ao salvar no Corel');
      }

      // Validar caminho retornado (case-insensitive para extensão)
      const caminhoLower = corelResult.caminhoSalvo?.toLowerCase() || '';
      if (!corelResult.caminhoSalvo || 
          !corelResult.caminhoSalvo.includes('\\') ||
          !caminhoLower.endsWith('.cdr')) {
        throw new Error('Caminho de arquivo inválido retornado pelo CorelDRAW');
      }

      logger.info('[SAVE] Arquivo salvo no Corel', {
        caminho: corelResult.caminhoSalvo
      });

      // CHECKPOINT 2: Verificar abort após etapa 1
      if (operationIdRef.current && abortManager.isAborted(operationIdRef.current)) {
        logger.warn('[SAVE] Salvamento abortado após etapa 1 (Corel)', {
          reason: 'document_closed_after_corel'
        });
        await cleanupOnAbort();
        saveStateManager.setSaveState('cancelled');
        saveStateManager.setErrorMessage('Salvamento cancelado: documento foi fechado durante o processo');
        return;
      }

      // ETAPA 1.5: Validar metadados iniciais (já coletados antes do checklist)
      // ⚠️ IMPORTANTE: Metadados foram coletados ANTES do checklist abrir (ChecklistWindow.runPreparation)
      // Não coletar novamente aqui, pois o documento já está travado/salvo
      progressManager.setProgress(30);

      // Usar metadados iniciais que já foram coletados antes do checklist
      const initialMetadata = metadataManager.metadataSnapshotRef.current;
      if (!initialMetadata) {
        sendToTerminal('❌ [SAVE] Metadados iniciais não encontrados', {
          hasMetadataSnapshotRef: !!metadataManager.metadataSnapshotRef,
          hasInitialMetadataSnapshot: !!initialMetadataSnapshot
        });
        throw new Error('Metadados iniciais não encontrados. O documento deve ser preparado antes de salvar.');
      }

      // Validar objetos dos metadados iniciais
      // Suporta múltiplos formatos de resposta do backend C#:
      // - result.snapshot.objetos (formato padrão do metadados-objetos full)
      // - snapshot.objetos (se result já foi extraído)
      // - result.objetos (formato alternativo)
      // - objetos (formato flat legado)
      const resultBlock = initialMetadata?.result ?? initialMetadata;
      const objetos = resultBlock?.snapshot?.objetos ||  // C# retorna aqui
                      initialMetadata?.snapshot?.objetos || // Fallback direto
                      resultBlock?.objetos ||                // Formato alternativo
                      initialMetadata?.objetos ||            // Formato flat
                      [];
      
      if (objetos.length === 0) {
        // Log detalhado para debug
        sendToTerminal('❌ [DEBUG:METADATA] Nenhum objeto encontrado nos metadados iniciais', {
          hasMetadata: !!initialMetadata,
          hasResult: !!initialMetadata?.result,
          hasSnapshot: !!resultBlock?.snapshot,
          hasSnapshotDirect: !!initialMetadata?.snapshot,
          objetosInSnapshotResult: !!resultBlock?.snapshot?.objetos,
          objetosInSnapshotDirect: !!initialMetadata?.snapshot?.objetos,
          objetosInResult: !!resultBlock?.objetos,
          objetosInRoot: !!initialMetadata?.objetos,
          resultKeys: resultBlock ? Object.keys(resultBlock) : [],
          snapshotKeys: resultBlock?.snapshot ? Object.keys(resultBlock.snapshot) : [],
          snapshotDirectKeys: initialMetadata?.snapshot ? Object.keys(initialMetadata.snapshot) : [],
          metadataKeys: initialMetadata ? Object.keys(initialMetadata) : [],
          fullStructure: JSON.stringify(initialMetadata, null, 2).slice(0, 500)
        });
        throw new Error('Nenhum objeto encontrado nos metadados iniciais');
      }

      // Log de sucesso
      sendToTerminal('✅ [SAVE] Metadados iniciais validados com sucesso', {
        totalObjetos: objetos.length,
        fonte: resultBlock?.snapshot?.objetos ? 'result.snapshot.objetos ✅' : 
               initialMetadata?.snapshot?.objetos ? 'snapshot.objetos (direto)' :
               resultBlock?.objetos ? 'result.objetos' : 
               'objetos (flat)',
        primeiroObjeto: objetos[0]?.staticId || objetos[0]?.guid || 'sem ID',
        note: 'Metadados coletados antes do checklist (ChecklistWindow.runPreparation)'
      });

      // CHECKPOINT 3: Verificar abort após validação de metadados
      if (operationIdRef.current && abortManager.isAborted(operationIdRef.current)) {
        logger.warn('[SAVE] Salvamento abortado após etapa 1.5 (Validação de Metadados)', {
          reason: 'document_closed_after_metadata_validation'
        });
        await cleanupOnAbort();
        saveStateManager.setSaveState('cancelled');
        saveStateManager.setErrorMessage('Salvamento cancelado: documento foi fechado durante validação de metadados');
        return;
      }

      // Checkpoint após metadados - silencioso

      // Atualizar fileInfo
      fileInfoManager.updateFileInfoFromSaveData();

      // Marcar resposta recebida
      progressManager.setProgress(50);
      progressManager.markResponseReceived();

      // Aguardar antes de enviar para API
      await new Promise(resolve => setTimeout(resolve, DELAY_BEFORE_API));

      // Checkpoint antes de API - silencioso

      // CHECKPOINT 4: Verificar abort antes da API
      if (operationIdRef.current && abortManager.isAborted(operationIdRef.current)) {
        logger.warn('[SAVE] Salvamento abortado antes de enviar para API', {
          reason: 'document_closed_before_api'
        });
        await cleanupOnAbort();
        saveStateManager.setSaveState('cancelled');
        saveStateManager.setErrorMessage('Salvamento cancelado: documento foi fechado antes de registrar na API');
        return;
      }

      // ETAPA 2: Enviar para API
      logger.info('[SAVE] Enviando para API');
      progressManager.setProgress(60);

      const apiSuccess = await ApiSaveService.sendOnce({
        saveData: fileInfoManager.saveData,
        saveDataRef: fileInfoManager.saveDataRef,
        savedFilePathRef: fileInfoManager.savedFilePathRef,
        lastSaveMetaRef: metadataManager.lastSaveMetaRef,
        role,
        idemKeyRef,
        setErrorMessage: saveStateManager.setErrorMessage,
        showConfirmDialog: saveStateManager.showConfirmDialog,
        apiSentRef,
        sendToProduction: async () => {
          const payload = generateApiRequestPayload({
            saveData: fileInfoManager.saveData,
            saveDataRef: fileInfoManager.saveDataRef,
            savedFilePathRef: fileInfoManager.savedFilePathRef.current,
            lastSaveMetaRef: metadataManager.lastSaveMetaRef, // ✅ CORRIGIDO: passar REF, não .current
            isExternalUser,
            role
          });
          if (!payload) return false;

          const fileHash = metadataManager.getFileHash();
          return await ApiSaveService.sendToProduction({
            payload,
            idemKeyRef,
            fileHash,
            setErrorMessage: saveStateManager.setErrorMessage,
            showConfirmDialog: saveStateManager.showConfirmDialog
          });
        }
      });

      if (!apiSuccess) {
        throw new Error('Falha ao enviar para API');
      }

      logger.info('[SAVE] Dados enviados para API');

      // SUCESSO COMPLETO
      await new Promise(resolve => setTimeout(resolve, 200));
      progressManager.complete();

      // Determinar próximo estado
      const nextState: SaveState = isExternalUser ? 'uploading' : 'success';
      saveStateManager.setSaveState(nextState);

      logger.info('[SAVE] Pipeline concluído com sucesso', {
        nextState
      });

      // Atualiza a lista de pedidos imediatamente (mesmo padrão do useRefuseAction):
      // otimista local (sys_save no item + par vinculado) e refetch forçado em
      // background. Sem isso o card só sai de "pendentes" no próximo polling (60s).
      try {
        const orcId = fileInfoManager.saveData?.pedido?.orc_id;
        const oitId = fileInfoManager.saveData?.pedido?.oit_id;
        if (orcId != null && oitId != null) {
          markItemArquivoSaved?.(Number(orcId), Number(oitId));
        }
      } catch (optimisticError) {
        logger.warn('[SAVE] Falha no update otimista pós-save (refetch cobre)', {
          error: (optimisticError as Error)?.message
        });
      }
      void fetchPedidos?.(true)?.catch((refetchError) => {
        logger.warn('[SAVE] Refetch pós-save falhou (polling cobre)', {
          error: (refetchError as Error)?.message
        });
      });

      // ⚠️ IMPORTANTE: Lock e monitor NÃO são reativados aqui!
      // Conforme documentação (save-pipeline.md):
      // - Lock permanece DESATIVADO durante todo o fluxo de sucesso
      // - Documento permanece ABERTO para exibir MessageModal (Fase 6)
      // - Fechamento acontece apenas quando usuário copiar mensagem (Fase 7)
      // - Cleanup é feito automaticamente pela cascata de fechamento

    } catch (e: any) {
      logger.error('[SAVE] Erro no pipeline', {
        error: e.message,
        stack: e.stack
      });

      progressManager.stopProgress();
      
      // ✅ Restaurar recursos APENAS em caso de ERRO (não em sucesso)
      // Conforme documentação (save-pipeline.md, linhas 345-361):
      // - Lock é reativado para permitir edição
      // - Monitor é reiniciado para detectar mudanças
      // - Usuário pode tentar novamente sem refazer checklist
      await restoreResourcesOnError();

      saveStateManager.setError(e.message || 'Erro desconhecido durante salvamento');
      progressManager.markResponseReceived();
    } finally {
      // Limpar timeout
      if (timeoutCheckRef.current) {
        clearTimeout(timeoutCheckRef.current);
        timeoutCheckRef.current = null;
      }
      
      // ✅ Desregistrar operação quando o pipeline terminar (sucesso ou erro)
      // Movido do cleanup do useEffect para cá para evitar remoção prematura pelo React StrictMode
      if (operationIdRef.current) {
        abortManager.unregisterOperation(operationIdRef.current);
        operationIdRef.current = null; // Limpar ref para evitar double-unregister
      }
      
      // Marcar pipeline como não-executando
      pipelineExecutingRef.current = false;
    }
  }, [
    fileInfoManager,
    metadataManager,
    progressManager,
    saveStateManager,
    role,
    notify,
    idemKeyRef,
    isExternalUser,
    restoreResourcesOnError,
    cleanupOnAbort,
    operationIdRef,
    fetchPedidos,
    markItemArquivoSaved
  ]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Carregar file info na montagem (apenas uma vez)
  useEffect(() => {
    logger.debug('[SAVE] Carregando file info');
    fileInfoManager.loadFileInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas na montagem - loadFileInfo é estável

  // Pipeline principal
  useEffect(() => {
    if (saveStateManager.saveState === 'saving') {
      // Guard: prevenir múltiplas execuções simultâneas
      if (pipelineExecutingRef.current) {
        logger.warn('[SAVE] Pipeline já está executando, ignorando nova tentativa');
        return;
      }

      logger.info('[SAVE] Iniciando pipeline de salvamento');
      pipelineExecutingRef.current = true;

      // Registrar operação para abort
      const workingData = fileInfoManager.saveDataRef.current || fileInfoManager.saveData;
      if (workingData?.pedido?.orc_id && workingData?.pedido?.oit_id) {
        const orcId = workingData.pedido.orc_id;
        const oitId = workingData.pedido.oit_id;
        
        const validation = validateOrderIds(orcId, oitId);
        if (validation.areBothValid && validation.operationId) {
          operationIdRef.current = validation.operationId;
          abortManager.registerOperation(validation.operationId);
          
          logger.info('[SAVE] Operação registrada para abort', {
            operationId: validation.operationId
          });
        } else {
          logger.warn('[SAVE] IDs inválidos - abort não será registrado', {
            orcId,
            oitId,
            isValidOrcId: validation.isValidOrcId,
            isValidOitId: validation.isValidOitId
          });
        }
      }

      // Iniciar progresso
      progressManager.startProgress();

      // Executar pipeline (async, mas não aguardamos aqui)
      executeSavePipeline().finally(() => {
        pipelineExecutingRef.current = false;
      });

      // Timeout de segurança (busca do painel admin, fallback: 1h)
      const timeoutMs = getActionTimeout('salvararquivo');
      const timeoutMinutes = Math.round(timeoutMs / 60000);
      timeoutCheckRef.current = setTimeout(() => {
        logger.warn(`[SAVE] Timeout atingido após ${timeoutMinutes} minutos`);
        saveStateManager.setError(`Tempo limite excedido para salvamento (${timeoutMinutes} min)`);
        progressManager.stopProgress();
        pipelineExecutingRef.current = false;
      }, timeoutMs);
    }
  }, [
    saveStateManager.saveState,
    executeSavePipeline
  ]); // Dependências mínimas: apenas saveState e função (ambas estáveis)

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      progressManager.stopProgress();
      if (timeoutCheckRef.current) {
        clearTimeout(timeoutCheckRef.current);
      }
      // ⚠️ NÃO desregistrar operação aqui!
      // O React StrictMode executa cleanup durante re-renders,
      // o que removia a operação prematuramente.
      // A operação é desregistrada no finally do executeSavePipeline.
    };
  }, [progressManager]);

  // ============================================================================
  // DERIVADOS
  // ============================================================================

  const clientMessage = useMemo(() => {
    try {
      return generateClientMessage();
    } catch {
      return '';
    }
  }, [generateClientMessage]);

  const apiDebugPayload = useMemo(() => {
    try {
      return generateDebugPayload(
        fileInfoManager.saveData,
        fileInfoManager.fileInfo,
        isExternalUser,
        role
      );
    } catch {
      return null;
    }
  }, [fileInfoManager.saveData, fileInfoManager.fileInfo, isExternalUser, role]);

  // Log de inicialização (apenas uma vez na montagem)
  useEffect(() => {
    logger.info('[SAVE] Hook refatorado pronto');
  }, []);

  // ============================================================================
  // INTERFACE PÚBLICA (23 EXPORTS - IDÊNTICA AO ORIGINAL)
  // ============================================================================

  return {
    // Estados (9)
    saveState: saveStateManager.saveState,
    progress: progressManager.progress,
    fileInfo: fileInfoManager.fileInfo,
    showMetadataModal: metadataManager.showMetadataModal,
    showClientMessage,
    showDebugInfo,
    errorMessage: saveStateManager.errorMessage,
    isClosing: saveStateManager.isClosing,
    needConfirmUpdate: saveStateManager.needConfirmUpdate,

    // Derivados (2)
    clientMessage,
    apiDebugPayload,

    // Actions (12)
    setShowDebugInfo,
    handleClose,
    handleShowClientMessage,
    handleExportComplete,
    confirmUpdate: saveStateManager.confirmUpdate,
    cancelUpdate: saveStateManager.cancelUpdate,
    getErrorTitle,
    handleRetry,
  } as const;
}
